import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import PropTypes from "prop-types";
import axios from "axios";
import { signIn, useSession } from "next-auth/react";
import { useRouter } from "next/router";
import ButtonWithLoading from "../form-fields/ButtonWithLoading";
import Input from "../form-fields/Input";
import RateLimitError from "../errors/RateLimitError";
import FacebookIcon from "../svg/FacebookIcon";
import GoogleIcon from "../svg/GoogleIcon";

const hasGoogleAuth = false;
const hasFacebookAuth = false;

export default function LoginForm({ onSuccess }) {
  const { data: session } = useSession();
  const router = useRouter();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const t = useTranslations("Account");

  // Remove the token handling effect since it's now in _app.js
  useEffect(() => {
    if (session?.wordpressToken && onSuccess) {
      onSuccess({ token: session.wordpressToken });
    }
  }, [session, onSuccess]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGoogleLogin = async () => {
    setError("");
    setIsGoogleLoading(true);
    try {
      const redirectUrl = localStorage.getItem('redirectUrl') || '/my-account';
      const result = await signIn('google', {
        redirect: false,
        callbackUrl: window.location.origin,
      });

      if (result?.error) {
        setError(t("error_google_login"));
        setIsGoogleLoading(false);
      } else if (result?.ok) {
        // Get the Google token and user info
        const response = await axios.post('/api/auth/google-callback', {
          accessToken: result.token,
          email: result.user?.email,
          name: result.user?.name,
        });

        if (response.data.token) {
          if (onSuccess) {
            onSuccess(response.data);
          }
          localStorage.removeItem('redirectUrl');
          router.push(redirectUrl);
        }
      }
    } catch (error) {
      setError(t("error_google_login"));
      console.error('Google login error:', error);
      setIsGoogleLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setError("");
    setIsFacebookLoading(true);
    try {
      const redirectUrl = localStorage.getItem('redirectUrl') || '/my-account';
      const result = await signIn('facebook', {
        redirect: false,
        callbackUrl: window.location.origin,
      });

      if (result?.error) {
        setError(t("error_facebook_login"));
        setIsFacebookLoading(false);
      } else if (result?.ok) {
        // Get the Facebook access token and user info
        const response = await axios.post('/api/auth/facebook-callback', {
          accessToken: result.token,
          email: result.user?.email,
          name: result.user?.name,
        });

        if (response.data.token) {
          // Remove token storage since it's handled in _app.js
          if (onSuccess) {
            onSuccess(response.data);
          }
          // Clear the redirect URL and navigate
          localStorage.removeItem('redirectUrl');
          router.push(redirectUrl);
        }
      }
    } catch (error) {
      setError(t("error_facebook_login"));
      console.error('Facebook login error:', error);
      setIsFacebookLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsRateLimited(false);
    setIsLoading(true);

    try {
      const response = await axios.post("/api/auth/login", formData);

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        if (onSuccess) {
          onSuccess(response.data);
        }
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setIsRateLimited(true);
      } else {
        setError(error.response?.data?.message || t("error_invalid_credentials"));
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-2">
        <Input
          title={t("user_or_email")}
          type="text"
          name="username"
          value={formData.username}
          handleChange={handleChange}
          required
          data-testid="login-username"
        />
        <Input
          title={t("password")}
          type="password"
          name="password"
          value={formData.password}
          handleChange={handleChange}
          required
          data-testid="login-password"
        />
        {isRateLimited && <RateLimitError />}
        {error && !isRateLimited && (
          <div className="text-red-500 text-sm mt-2" data-testid="login-error">
            {error}
          </div>
        )}
        <ButtonWithLoading
          type="submit"
          isLoading={isLoading}
          text={t("login")}
          loadingText={t("loading")}
          className="w-full"
          data-testid="login-submit"
        />
      </form>

      {(hasGoogleAuth || hasFacebookAuth) && (
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t("or")}</span>
            </div>
          </div>

          <div className="space-y-3">
            {hasGoogleAuth && (
              <ButtonWithLoading
                type="button"
                onClick={handleGoogleLogin}
                isLoading={isGoogleLoading}
                text={
                  <div className="flex items-center justify-center gap-2">
                    <GoogleIcon size="20" className="mt-0.5" />
                    <span>{t("continue_with_google")}</span>
                  </div>
                }
                loadingText={t("loading")}
                className="w-full bg-white hover:bg-gray-50 text-gray-600 border border-gray-300"
                data-testid="google-login"
              />
            )}

            {hasFacebookAuth && (
              <ButtonWithLoading
                type="button"
                onClick={handleFacebookLogin}
                isLoading={isFacebookLoading}
                text={
                  <div className="flex items-center justify-center gap-2">
                    <FacebookIcon fill="#fff" size="20" className="mt-0.5" />
                    <span>{t("continue_with_facebook")}</span>
                  </div>
                }
                loadingText={t("loading")}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="facebook-login"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

LoginForm.propTypes = {
  onSuccess: PropTypes.func,
};